// Early, zero-cost identity check for a pasted LinkedIn URL: most vanity slugs carry the person's
// name ("/in/sven-rathjen-1a2b3c"), so a slug that clearly names SOMEONE ELSE is worth a warning
// before any money is spent on a fetch. Deliberately quiet when it can't judge — encoded ids
// ("/in/ACoAA…", the case that caused the original mis-merge), numeric junk, or a missing candidate
// name all return null. The authoritative check is the server-side identity gate at fetch time;
// this is just the cheap first tripwire.

const GERMAN_FOLD = [["ä", "ae"], ["ö", "oe"], ["ü", "ue"], ["ß", "ss"]];

function stripDiacritics(value) {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").normalize("NFC");
}

// All spellings under which we accept a name token: as written, diacritics stripped, and
// German-transliterated. Never fold the other direction ("ue" → "u" would corrupt "Bauer").
function variants(token) {
  const out = new Set([token, stripDiacritics(token)]);
  let folded = token;
  for (const [from, to] of GERMAN_FOLD) folded = folded.split(from).join(to);
  out.add(folded);
  return [...out];
}

function nameTokens(...values) {
  return values
    .filter(Boolean)
    .flatMap((v) => v.split(/[\s\-‑,.]+/))
    .map((t) => t.toLowerCase().replace(/[^\p{L}]/gu, ""))
    .filter((t) => t.length >= 2);
}

/**
 * Returns a display name ("Sven Rathjen") when the URL's slug clearly carries a person's name that
 * shares nothing with the candidate's — or null when it matches, can't be judged, or isn't a name.
 */
export function linkedInSlugMismatch(url, { firstName, lastName, name } = {}) {
  const m = /linkedin\.com\/in\/([^/?#]+)/i.exec(url ?? "");
  if (!m) return null;
  let slug;
  try {
    slug = decodeURIComponent(m[1]);
  } catch {
    return null;
  }
  // Encoded member ids carry no name at all. LinkedIn's are "ACoAA…"-prefixed; be generous and
  // also skip anything long and caseless-mixed enough to look machine-generated.
  if (/^ACoAA/i.test(slug)) return null;

  // Slug words: alphabetic segments only. A segment containing any digit is LinkedIn's
  // uniquifying id/hash suffix ("1a2b3c", "8f"), never a name part — drop it whole.
  const slugTokens = slug
    .split(/[-_]+/)
    .filter((t) => !/\d/.test(t))
    .map((t) => t.toLowerCase().replace(/[^\p{L}]/gu, ""))
    .filter((t) => t.length >= 3);
  if (slugTokens.length === 0) return null;

  const candidateTokens = nameTokens(firstName, lastName, name);
  if (candidateTokens.length === 0) return null;
  // Without a usable last name there is nothing reliable to contradict.
  if (nameTokens(lastName, name).length === 0) return null;

  // Compatible when any candidate-name spelling appears in the slug — as a whole token, a prefix
  // (truncated slugs), or inside a mashed token ("jdoe", "simongiuliani").
  const compatible = candidateTokens.some((ct) =>
    variants(ct).some((cv) =>
      slugTokens.some((st) => variants(st).some((sv) =>
        sv === cv || sv.startsWith(cv) || (cv.length >= 4 && sv.includes(cv))))));
  if (compatible) return null;

  return slugTokens.map((t) => t.charAt(0).toUpperCase() + t.slice(1)).join(" ");
}
